import { Box, Button, Card, CardBody, Flex, Progress, Text, useToast } from '@chakra-ui/react';
import { uploadAction } from 'actions';
import { UPLOAD_FILES_STATUS } from 'helpers/enum';
import { formatBytes } from 'helpers/file';
import useUpload from 'hooks/useUpload';
import { CheckCircle2, FileText, UploadCloud } from 'lucide-react';
import { useOrganizationUsageCurrent } from 'queries/useOrganization';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

// Per-file upload size cap (#77529-2). Files larger than this are rejected before upload.
export const MAX_UPLOAD_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

export const FILE_EXTENSION_UPLOAD_ALLOWED = [
  '.pdf',
  '.doc',
  '.docx',
  '.xlsx',
  '.xls',
  '.csv',
  '.ppt',
  '.pptx',
  '.tsv',
  '.md',
  '.txt',
];

const UploadInput = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useDispatch();
  const { uploadFiles, isUploading, progressValue } = useUpload();

  const { stats, files, status } = useSelector((state) => state.upload);
  const { data: usage } = useOrganizationUsageCurrent();

  // Persistent post-upload summary (#77529-1): the upload state is reset after FINISH, so we
  // snapshot the result locally and keep showing "N of M uploaded" until the next upload.
  const [summary, setSummary] = useState<{ success: number; fail: number; total: number } | null>(
    null
  );
  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.UPLOADING) setSummary(null);
    if (status === UPLOAD_FILES_STATUS.FINISH && stats.total > 0) {
      setSummary({ success: stats.uploadSuccess, fail: stats.uploadFail, total: stats.total });
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDrop = useCallback(
    (acceptedFiles: File[] = []) => {
      if (!acceptedFiles?.length) {
        toast({
          title: t('upload.noValidFilesFound'),
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
        return;
      }
      // Account storage quota (#77529-5): block an upload that would exceed the org's limit
      // (e.g. 1GB) instead of silently accepting 10GB. Both values arrive from the API in GB.
      const limitGB = usage?.limitation?.storage_gb;
      const usedGB = usage?.current_usage?.total_storage_gb ?? 0;
      const incomingGB =
        acceptedFiles.reduce((sum, f) => sum + (f?.size || 0), 0) / (1024 * 1024 * 1024);
      if (typeof limitGB === 'number' && limitGB > 0 && usedGB + incomingGB > limitGB) {
        toast({
          title: t('upload.exceedsQuota', {
            defaultValue:
              'Upload exceeds your {{limit}}GB storage limit ({{used}}GB used). Free up space or upload fewer files.',
            limit: limitGB,
            used: usedGB.toFixed(2),
          }),
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
        return;
      }
      uploadFiles(acceptedFiles);
    },
    [usage, uploadFiles, t, toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/msword': FILE_EXTENSION_UPLOAD_ALLOWED,
    },
    validator: blockSpecialFile,
  });

  function blockSpecialFile(file: File) {
    if (file.name?.includes('.dot')) {
      return {
        code: 'file-not-support',
        message: `Not support this type file!`,
      };
    }
    // Per-file size limit (#77529-2)
    if (file.size > MAX_UPLOAD_FILE_SIZE) {
      return {
        code: 'file-too-large',
        message: `${file.name} exceeds the ${formatBytes(MAX_UPLOAD_FILE_SIZE)} limit`,
      };
    }
    return null;
  }

  const totalSize = files?.reduce((sum, f) => sum + (f?.size || 0), 0) || 0;

  return (
    <Box>
      {/* Dropzone (mockup: .dropzone) */}
      <Box
        px={8}
        py={12}
        color='text.default'
        textAlign='center'
        bg={isDragActive ? 'primary.soft' : 'bg.surface'}
        borderWidth='2px'
        borderStyle='dashed'
        borderColor={isDragActive ? 'primary.solid' : 'border.strong'}
        borderRadius='xl'
        _hover={!isUploading ? { borderColor: 'primary.solid', bg: 'primary.soft' } : undefined}
        _focusVisible={{
          outline: 'none',
          borderColor: 'primary.solid',
          boxShadow: '0 0 0 3px var(--chakra-colors-primary-soft)',
        }}
        cursor={!isUploading ? 'pointer' : 'default'}
        transition='all 0.18s ease-out'
        aria-label={t('upload.dropHere')}
        role='button'
        tabIndex={0}
        {...(!isUploading && getRootProps())}
      >
        <input {...getInputProps()} type='file' id='filepicker' name='fileList' multiple />

        {/* Icon chip (mockup: .dropzone__icon) */}
        <Flex
          align='center'
          justify='center'
          w='64px'
          h='64px'
          mx='auto'
          mb={4}
          color='primary.solid'
          bg='primary.soft'
          borderRadius='xl'
        >
          <UploadCloud size={30} />
        </Flex>

        <Text color='text.default' fontFamily='heading' fontSize='md' fontWeight={600}>
          {t('upload.dropHere')}
        </Text>
        <Text mt={1} color='text.muted' fontSize='sm'>
          {t('upload.browse')}
        </Text>
      </Box>

      {/* Upload queue + overall progress (visible while uploading).
          Guarded on files.length so an empty queue card/row never shows. #77529-4 */}
      {isUploading && files && files.length > 0 && (
        <Box mt={6}>
          <Flex align='center' justify='space-between' mb={3}>
            <Text color='text.default' fontSize='sm' fontWeight={600}>
              {t('upload.uploadingProgress', {
                success: stats.uploadSuccess,
                total: stats.total,
              })}
            </Text>
            {files && files.length > 0 && (
              <Text color='text.muted' fontFamily='mono' fontSize='sm'>
                {files.length} · {formatBytes(totalSize)}
              </Text>
            )}
          </Flex>

          <Card
            bg='bg.surface'
            borderWidth='1px'
            borderColor='border.default'
            borderRadius='lg'
            shadow='ds_sm'
            variant='unstyled'
          >
            <CardBody p={4}>
              <Progress
                h='8px'
                borderRadius='full'
                aria-label={t('upload.uploadingProgress', {
                  success: stats.uploadSuccess,
                  total: stats.total,
                })}
                value={progressValue}
                variant='ds'
              />

              {files && files.length > 0 && (
                <Box mt={4} borderColor='border.default' borderTopWidth='1px'>
                  {files.map((f, idx) => (
                    <Flex
                      key={`${f.name}-${idx}`}
                      align='center'
                      gap={3}
                      py={3}
                      borderColor='border.default'
                      borderBottomWidth='1px'
                      _last={{ borderBottomWidth: 0 }}
                    >
                      <Flex
                        align='center'
                        justify='center'
                        flexShrink={0}
                        w='32px'
                        h='32px'
                        color='primary.solid'
                        bg='primary.soft'
                        borderRadius='md'
                      >
                        <FileText size={16} />
                      </Flex>
                      <Text
                        flex='1'
                        overflow='hidden'
                        minW={0}
                        color='text.default'
                        fontSize='sm'
                        fontWeight={500}
                        whiteSpace='nowrap'
                        textOverflow='ellipsis'
                      >
                        {f.name}
                      </Text>
                      <Text flexShrink={0} color='text.muted' fontFamily='mono' fontSize='sm'>
                        {formatBytes(f.size)}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              )}
            </CardBody>
          </Card>
        </Box>
      )}

      {/* Post-upload summary — stays visible after the queue clears so the user can see how many
          files were uploaded successfully, instead of the screen going blank. #77529-1 */}
      {!isUploading && summary && (
        <Flex
          align='center'
          gap={3}
          mt={6}
          p={4}
          bg='bg.surface'
          borderWidth='1px'
          borderColor='border.default'
          borderRadius='lg'
          shadow='ds_sm'
        >
          <Flex
            align='center'
            justify='center'
            flexShrink={0}
            w='32px'
            h='32px'
            color={summary.fail > 0 ? 'state.warning' : 'state.success'}
            bg={summary.fail > 0 ? 'state.warningSoft' : 'state.successSoft'}
            borderRadius='md'
          >
            <CheckCircle2 size={18} />
          </Flex>
          <Box flex='1' minW={0}>
            <Text color='text.default' fontSize='sm' fontWeight={600}>
              {t('upload.summaryTitle', {
                defaultValue: '{{success}} of {{total}} files uploaded',
                success: summary.success,
                total: summary.total,
              })}
            </Text>
            {summary.fail > 0 && (
              <Text color='state.warning' fontSize='xs'>
                {t('upload.summaryFailed', {
                  defaultValue: '{{fail}} failed',
                  fail: summary.fail,
                })}
              </Text>
            )}
          </Box>
          <Button
            onClick={() => {
              setSummary(null);
              dispatch(uploadAction.resetUpload());
            }}
            size='ds-sm'
            variant='ds-ghost'
          >
            {t('common.done', { defaultValue: 'Done' })}
          </Button>
        </Flex>
      )}
    </Box>
  );
};

export default UploadInput;
